import { Button } from "@package/subframe/components/Button";
import { Table } from "@package/subframe/components/Table";
import { FeatherThermometer } from "@subframe/core";
import { format, formatDistanceToNow } from "date-fns";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { TintedEntityIcon } from "@/components/ui/entity-icon";
import { SortableHeaderCell } from "@/components/ui/sortable-header-cell";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import type { MonitoringEntryDto } from "@/generated/model/monitoringEntryDto";
import {
  formatValue,
  isPassFail,
  isUntargeted,
  monitoringCategorySlug,
  type MonitoringSortColumn,
  type MonitoringView,
  rangeLabel,
  recordedByLabel,
  resolvedByLabel,
  type SortDir,
  targetLabel,
} from "@/lib/monitoring";
import {
  MONITORING_UNTARGETED_PILL,
  monitoringAlertPill,
} from "@/lib/workspace/status-pill";

interface MonitoringTableProps {
  entries: MonitoringEntryDto[];
  /**
   * Which list is being rendered. A single prop rather than per-column flags:
   * the header and the body branch on it together, so they can't disagree and
   * misalign the columns.
   */
  view: MonitoringView;
  activeColumn?: MonitoringSortColumn;
  activeDir: SortDir;
  onSort: (column: MonitoringSortColumn) => void;
  onView: (entry: MonitoringEntryDto) => void;
  /** Whether the row has somewhere to go — gates both View and the row click. */
  canView: (entry: MonitoringEntryDto) => boolean;
  /** Clears a failed-check alert. Omit to hide the acknowledge affordance. */
  onAcknowledge?: (alertId: string) => void;
  /** Alert id currently being acknowledged, for the pending button state. */
  acknowledgingAlertId?: string | null;
}

/** Value chip tinted by the range judgement, like the design's 0°C / -18°C chips. */
function ValueChip({ entry }: { entry: MonitoringEntryDto }) {
  // Untargeted rows are neutral: they were never judged, so the success tint
  // that `isOutOfRange === false` otherwise produces would read as a green pass.
  const toneClass = isUntargeted(entry)
    ? "bg-neutral-100 text-subtext-color"
    : entry.isOutOfRange
      ? "bg-error-50 text-error-600"
      : "bg-success-50 text-success-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-caption-bold font-caption-bold ${toneClass}`}
    >
      {formatValue(entry)}
    </span>
  );
}

export function MonitoringTable({
  entries,
  view,
  activeColumn,
  activeDir,
  onSort,
  onView,
  canView,
  onAcknowledge,
  acknowledgingAlertId,
}: MonitoringTableProps) {
  // Every row on the resolved list is Resolved by construction, so the status
  // pill carries no information there and gives way to the resolution audit.
  const showsResolution = view === "resolved-alerts";
  // Only the readings list records an act someone performed; an alert episode is
  // opened by the system, so there is nobody to name on the other two views.
  const showsRecorder = view === "readings";

  return (
    <Table
      className={showsRecorder ? TABLE_MIN_WIDTH.lg : TABLE_MIN_WIDTH.md}
      header={
        <Table.HeaderRow>
          <SortableHeaderCell
            column="alert"
            label="Alert"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="location"
            label="Location"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="value"
            label="Value"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="triggered"
            label="Triggered"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          {/* Unsortable: history rows are merged from three queries and ordered
              in memory, so there is no MonitoringSortColumn to send. */}
          {showsRecorder && <Table.HeaderCell>Completed by</Table.HeaderCell>}
          {showsResolution ? (
            <>
              <SortableHeaderCell
                column="resolved"
                label="Resolved"
                activeColumn={activeColumn}
                activeDir={activeDir}
                onSort={onSort}
              />
              <Table.HeaderCell>Resolved by</Table.HeaderCell>
            </>
          ) : (
            <SortableHeaderCell
              column="status"
              label="Status"
              activeColumn={activeColumn}
              activeDir={activeDir}
              onSort={onSort}
            />
          )}
          <Table.HeaderCell variant="aligned-right">Actions</Table.HeaderCell>
        </Table.HeaderRow>
      }
    >
      {entries.map((entry) => {
        const range = rangeLabel(entry);
        const target = targetLabel(entry);
        const triggeredAt = new Date(entry.triggeredAt);
        const resolvedAt = entry.resolvedAt ? new Date(entry.resolvedAt) : null;
        const acknowledgeableId = entry.acknowledgeableAlertId;
        const viewable = canView(entry);

        return (
          <Table.Row
            key={`${entry.source}-${entry.id}`}
            clickable={viewable}
            onClick={viewable ? () => onView(entry) : undefined}
          >
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex items-center gap-3">
                <TintedEntityIcon
                  entity="monitoring"
                  slug={monitoringCategorySlug(entry.logTypeSlug)}
                  fallbackIcon={<FeatherThermometer />}
                  title={entry.alertName}
                />
                <div className="flex flex-col items-start">
                  <span className="text-body-bold font-body-bold text-neutral-900">
                    {entry.alertName}
                  </span>
                  <span
                    className={`text-caption font-caption ${range.className}`}
                  >
                    {range.label}
                  </span>
                </div>
              </div>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {entry.locationName ?? "—"}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex flex-col items-start gap-0.5">
                <ValueChip entry={entry} />
                {target && (
                  <span className="text-caption font-caption text-subtext-color">
                    {target}
                  </span>
                )}
              </div>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex flex-col items-start">
                <span className="text-caption font-caption text-neutral-900">
                  {format(triggeredAt, "d MMM yyyy, h:mm a")}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  {formatDistanceToNow(triggeredAt, { addSuffix: true })}
                </span>
              </div>
            </Table.Cell>
            {showsRecorder && (
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <span className="text-caption font-caption text-neutral-900">
                  {recordedByLabel(entry)}
                </span>
              </Table.Cell>
            )}
            {showsResolution ? (
              <>
                <Table.Cell className="h-14 grow shrink-0 basis-0">
                  {resolvedAt ? (
                    <div className="flex flex-col items-start">
                      <span className="text-caption font-caption text-neutral-900">
                        {format(resolvedAt, "d MMM yyyy, h:mm a")}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        {formatDistanceToNow(resolvedAt, { addSuffix: true })}
                      </span>
                    </div>
                  ) : (
                    <span className="text-caption font-caption text-subtext-color">
                      —
                    </span>
                  )}
                </Table.Cell>
                <Table.Cell className="h-14 grow shrink-0 basis-0">
                  <span className="text-caption font-caption text-neutral-900">
                    {resolvedByLabel(entry)}
                  </span>
                </Table.Cell>
              </>
            ) : (
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <WorkspaceStatusBadge
                  pill={
                    isUntargeted(entry)
                      ? MONITORING_UNTARGETED_PILL
                      : monitoringAlertPill(entry.status, isPassFail(entry))
                  }
                />
              </Table.Cell>
            )}
            <Table.Cell
              className="h-14 grow shrink-0 basis-0"
              variant="aligned-right"
            >
              {/* Stop clicks here from also triggering the row's navigation. */}
              <div
                className="flex items-center justify-end gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Acknowledging is the only way to clear a failed check, so
                    without this button the amber dashboard row is permanent. */}
                {acknowledgeableId && onAcknowledge && (
                  <Button
                    variant="brand-secondary"
                    loading={acknowledgingAlertId === acknowledgeableId}
                    disabled={Boolean(acknowledgingAlertId)}
                    onClick={() => onAcknowledge(acknowledgeableId)}
                  >
                    Acknowledge
                  </Button>
                )}
                {/* NAU-411 AC15: a failure only clears once a newer check has
                    passed, so until then there is nothing to acknowledge. */}
                {entry.awaitingRecheck && onAcknowledge && (
                  <span className="text-caption font-caption text-subtext-color whitespace-nowrap">
                    Awaiting re-check
                  </span>
                )}
                {viewable && (
                  <Button
                    variant="neutral-primary"
                    onClick={() => onView(entry)}
                  >
                    View
                  </Button>
                )}
              </div>
            </Table.Cell>
          </Table.Row>
        );
      })}
    </Table>
  );
}
